import { FC, useState } from 'react';

type PaginationProps = {
  totalItems: number;
  pageSize: number;
  currentPage: number;
  goToPage: (page: number) => void;
};

type PageItem = {
  id: string;
  value: number | string;
};

const buttonBase =
  'w-10 h-10 flex items-center justify-center border border-gray-300 rounded bg-white transition-colors text-xl';

const Pagination: FC<PaginationProps> = ({
  totalItems,
  pageSize,
  currentPage,
  goToPage,
}) => {
  const [inputValue, setInputValue] = useState('');
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) {
    return null;
  }

  const getPageNumbers = (): PageItem[] => {
    const pages: PageItem[] = [];

    pages.push({ id: 'page-1', value: 1 });

    if (currentPage > 2) {
      pages.push({ id: 'dots-left', value: '...' });
    }

    if (currentPage !== 1 && currentPage !== totalPages) {
      pages.push({ id: `page-${currentPage}`, value: currentPage });
    }

    if (currentPage < totalPages - 1) {
      pages.push({ id: 'dots-right', value: '...' });
    }

    if (totalPages > 1) {
      pages.push({ id: `page-${totalPages}`, value: totalPages });
    }

    return pages;
  };

  const pages = getPageNumbers();

  const handlePrevious = (): void => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const handleNext = (): void => {
    if (currentPage < totalPages) {
      goToPage(currentPage + 1);
    }
  };

  const handleGoToPage = (): void => {
    const pageNum = parseInt(inputValue, 10);
    if (!Number.isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      goToPage(pageNum);
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const getPageButtonClasses = (page: number | string): string => {
    if (page === currentPage) {
      return 'bg-white border-honoursOrange text-honoursOrange font-medium';
    }
    if (page === '...') {
      return 'border-transparent text-gray-400 cursor-default';
    }
    return 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50';
  };

  return (
    <div className="flex flex-wrap items-center justify-end gap-2 mt-4 mx-4 md:mx-8 mb-4">
      <button
        className={`${buttonBase} ${
          currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
        onClick={handlePrevious}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        ‹
      </button>

      {pages.map(({ id, value }) => (
        <button
          key={id}
          className={`min-w-10 h-10 px-3 flex items-center justify-center border rounded transition-colors text-sm ${getPageButtonClasses(value)}`}
          onClick={() => typeof value === 'number' && goToPage(value)}
          disabled={value === '...'}
        >
          {value}
        </button>
      ))}

      <button
        className={`${buttonBase} ${
          currentPage === totalPages
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-700 hover:bg-gray-50'
        }`}
        onClick={handleNext}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        ›
      </button>

      <div className="flex items-center gap-2 ml-4 border-l border-gray-300 pl-4">
        <input
          type="number"
          min="1"
          max={totalPages}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyUp={handleKeyPress}
          placeholder="Page"
          className="w-16 h-10 px-2 text-sm text-center border border-gray-300 rounded focus:outline-none focus:border-gray-400 transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          onClick={handleGoToPage}
          disabled={!inputValue}
          className={`px-3 h-10 text-sm rounded border transition-colors ${
            !inputValue
              ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50'
              : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
          }`}
        >
          Go
        </button>
      </div>
    </div>
  );
};

export default Pagination;
